import moment from "moment";
import {
  getLeaveInfoForUserDate,
  getPartialLeaveCalendarTitle,
  getPartialLeaveComplianceStatus,
  isFullLeaveDay,
  isPartialLeaveDay,
  userMeetsDailyWorkRequirement,
} from "./dailyWorkLeave";
import { isDailyWorkUpdatePending } from "./dailyWorkGracePeriod";
import { isPublicHolidayDate, buildHolidayDateSet } from "./publicHolidays";

/**
 * Count filled / missed / pending workdays for a calendar month.
 * Weekends are excluded. The range end is capped at yesterday (inclusive).
 */
export function computeDailyWorkUpdateStats({
  calendarTasks,
  calendarLeaveByDate,
  publicHolidaysByDate,
  monthDate,
  targetUserId,
  referenceDate = moment(),
}) {
  const monthStart = moment(monthDate).startOf("month");
  const monthEnd = moment(monthDate).endOf("month");
  const yesterday = moment(referenceDate).subtract(1, "day").endOf("day");

  let rangeEnd = monthEnd;
  if (rangeEnd.isAfter(yesterday)) {
    rangeEnd = yesterday;
  }

  if (rangeEnd.isBefore(monthStart, "day")) {
    return { workdays: 0, filled: 0, missed: 0, pending: 0 };
  }

  const userIdStr =
    targetUserId != null && targetUserId !== "" ? String(targetUserId) : null;

  let workdays = 0;
  let filled = 0;
  let missed = 0;
  let pending = 0;

  const holidayDates = buildHolidayDateSet(publicHolidaysByDate);

  for (
    let m = moment(monthStart);
    m.isSameOrBefore(rangeEnd, "day");
    m.add(1, "day")
  ) {
    const dayOfWeek = m.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    const dateStr = m.format("YYYY-MM-DD");
    if (isPublicHolidayDate(publicHolidaysByDate, dateStr)) continue;

    workdays += 1;
    const entries = calendarTasks?.[dateStr];
    const leaveInfo = getLeaveInfoForUserDate(
      calendarLeaveByDate,
      dateStr,
      userIdStr
    );
    const isFilled = userMeetsDailyWorkRequirement(
      entries,
      userIdStr,
      leaveInfo
    );

    if (isFilled) {
      filled += 1;
    } else if (isDailyWorkUpdatePending(dateStr, referenceDate, holidayDates)) {
      pending += 1;
    } else {
      missed += 1;
    }
  }

  return { workdays, filled, missed, pending };
}

export function getDailyWorkUpdateCompletionPercent(stats) {
  const { workdays, filled, teamSize } = stats || {};
  const denominator =
    teamSize > 0 ? workdays * teamSize : workdays;
  if (!denominator) return 0;
  return Math.round((filled / denominator) * 100);
}

export function userHasTasksOnDate(entries, userIdStr) {
  const list = Array.isArray(entries) ? entries : [];
  if (!userIdStr) {
    return list.some((entry) => (entry?.taskDetails?.length || 0) > 0);
  }
  const userEntry = list.find(
    (entry) => String(entry?.userDetails?.userId ?? "") === userIdStr
  );
  return (userEntry?.taskDetails?.length || 0) > 0;
}

export function getEmployeeDisplayName(profile) {
  const name = [profile?.firstname, profile?.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || profile?.email || "";
}

/**
 * Per-day filled / missed / pending lists for team calendar (admin "All team").
 */
export function getTeamDayComplianceByDate({
  calendarTasks,
  calendarLeaveByDate,
  publicHolidaysByDate,
  dateStr,
  teamProfiles,
  referenceDate = moment(),
}) {
  if (isPublicHolidayDate(publicHolidaysByDate, dateStr)) {
    return { filled: [], missed: [], pending: [] };
  }
  const profiles = Array.isArray(teamProfiles) ? teamProfiles : [];
  const entries = calendarTasks?.[dateStr];
  const todayKey = moment(referenceDate).format("YYYY-MM-DD");
  const holidayDates = buildHolidayDateSet(publicHolidaysByDate);
  const isPendingDay = isDailyWorkUpdatePending(dateStr, referenceDate, holidayDates);
  const isToday = dateStr === todayKey;

  const filled = [];
  const missed = [];
  const pending = [];

  for (const emp of profiles) {
    const id = String(emp?.userId ?? emp?.id ?? "");
    if (!id) continue;
    const name = getEmployeeDisplayName(emp) || `User ${id}`;
    const leaveInfo = getLeaveInfoForUserDate(calendarLeaveByDate, dateStr, id);
    const isCompliant = userMeetsDailyWorkRequirement(entries, id, leaveInfo);
    const complianceStatus = getPartialLeaveComplianceStatus({
      isCompliant,
      isPending: isPendingDay,
    });
    let displayLabel = name;
    if (isPartialLeaveDay(leaveInfo)) {
      displayLabel = `${name} (${getPartialLeaveCalendarTitle(
        leaveInfo,
        complianceStatus
      )})`;
    } else if (isFullLeaveDay(leaveInfo) && isCompliant) {
      displayLabel = `${name} (On leave)`;
    }
    const person = { id, name, displayLabel };
    if (isCompliant) {
      filled.push(person);
    } else if (isPendingDay) {
      pending.push(person);
    } else if (!isToday) {
      missed.push(person);
    }
  }

  return { filled, missed, pending };
}

export function formatComplianceNamesTooltip(people) {
  if (!Array.isArray(people) || !people.length) return "";
  return people.map((p) => p.displayLabel || p.name).join("\n");
}

/**
 * Team totals: each (employee × workday) counts toward filled / missed / pending.
 * Admins and executives should be omitted from teamUserIds before calling.
 */
export function computeTeamDailyWorkUpdateStats({
  calendarTasks,
  calendarLeaveByDate,
  publicHolidaysByDate,
  monthDate,
  teamUserIds,
  referenceDate = moment(),
}) {
  const ids = Array.isArray(teamUserIds)
    ? teamUserIds.map((id) => String(id)).filter(Boolean)
    : [];

  const monthStart = moment(monthDate).startOf("month");
  const monthEnd = moment(monthDate).endOf("month");
  const yesterday = moment(referenceDate).subtract(1, "day").endOf("day");

  let rangeEnd = monthEnd;
  if (rangeEnd.isAfter(yesterday)) {
    rangeEnd = yesterday;
  }

  if (rangeEnd.isBefore(monthStart, "day") || !ids.length) {
    return { teamSize: ids.length, workdays: 0, filled: 0, missed: 0, pending: 0 };
  }

  let workdays = 0;
  let filled = 0;
  let missed = 0;
  let pending = 0;
  const holidayDates = buildHolidayDateSet(publicHolidaysByDate);

  for (
    let m = moment(monthStart);
    m.isSameOrBefore(rangeEnd, "day");
    m.add(1, "day")
  ) {
    const dayOfWeek = m.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    const dateStr = m.format("YYYY-MM-DD");
    if (isPublicHolidayDate(publicHolidaysByDate, dateStr)) continue;

    workdays += 1;
    const entries = calendarTasks?.[dateStr];

    for (const userIdStr of ids) {
      const leaveInfo = getLeaveInfoForUserDate(
        calendarLeaveByDate,
        dateStr,
        userIdStr
      );
      const isFilled = userMeetsDailyWorkRequirement(
        entries,
        userIdStr,
        leaveInfo
      );
      if (isFilled) {
        filled += 1;
      } else if (isDailyWorkUpdatePending(dateStr, referenceDate, holidayDates)) {
        pending += 1;
      } else {
        missed += 1;
      }
    }
  }

  return { teamSize: ids.length, workdays, filled, missed, pending };
}
